import { useUserStore } from '@/stores/userStore'
import { mapAccount, type AtlasAccount } from './accountProfile'

/**
 * Access-token plumbing for the Atlas-backed auth client.
 *
 * The short-lived ES256 access token lives in the (persisted) user store and is
 * attached as `Authorization: Bearer …` to user-scoped `/api/*` calls. The
 * rotating refresh token is NOT here — it's an httpOnly cookie only our
 * `/api/accounts/*` proxy can read. `refreshAccessToken()` mints a new access
 * token from that cookie; callers use it on a 401.
 */

export function getAccessToken(): string | null {
  return useUserStore.getState().token
}

interface RefreshResponse {
  accessToken?: string
  account?: AtlasAccount
}

let inFlight: Promise<string | null> | null = null

/**
 * Exchange the httpOnly refresh cookie for a fresh access token (the server
 * rotates the cookie). De-duped so a burst of concurrent 401s triggers a single
 * refresh. Resolves to the new token, or null if the session is gone — in which
 * case the store is cleared so the UI drops to signed-out.
 */
export function refreshAccessToken(): Promise<string | null> {
  if (inFlight) return inFlight
  inFlight = (async () => {
    try {
      const res = await fetch('/api/accounts/refresh', { method: 'POST', credentials: 'same-origin' })
      if (!res.ok) {
        useUserStore.getState().clear()
        return null
      }
      const data = (await res.json().catch(() => ({}))) as RefreshResponse
      if (!data.accessToken) {
        useUserStore.getState().clear()
        return null
      }
      const store = useUserStore.getState()
      const user = data.account ? mapAccount(data.account) : store.user
      if (user) store.setSession(user, data.accessToken)
      return data.accessToken
    } catch {
      return null
    } finally {
      inFlight = null
    }
  })()
  return inFlight
}
